import {
  Alert,
  AlertTitle,
  Button,
  capitalize,
  CircularProgress,
  Step,
  StepButton,
  Stepper,
} from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { buildPermission, getCustomerId } from "common/utils/auth";
import { CalibrationStep } from "./steps/CalibrationStep";
import { ChooseArchetypeStep } from "./steps/ChooseArchetype";
import { CustomerSelector } from "portal/components/customers/CustomerSelector";
import { DriveInfoStep } from "./steps/DriveInfoStep";
import {
  editingSupported,
  tractorToViewModel,
  TractorViewModel,
  viewModelSchema,
  viewModelToTractor,
} from "./TractorViewModel";
import { Form, Formik } from "formik";
import { getRobotPath, getTractorsPath } from "portal/utils/routing";
import { HitchStep } from "./steps/HitchStep";
import { Loading } from "portal/components/Loading";
import { NameAndPhotoStep } from "./steps/NameAndPhotoStep";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { PtoStep } from "./steps/PtoStep";
import { ReviewStep } from "./steps/ReviewStep";
import { ScvStep } from "./steps/ScvStep";
import { typedPath } from "typed-path";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useCanSetDangerousParameters } from "../../permissions";
import { useFormikWizard } from "portal/utils/hooks/useFormikWizard";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { validateThen } from "portal/utils/forms";
import LockIcon from "@mui/icons-material/LockOutlined";
import React, { FC, useMemo, useState } from "react";

interface TractorFormProps {
  tractorId?: string;
  setDefaultOnSerial?: string;
}

const S = typedPath<TractorViewModel>();

export const TractorForm: FC<TractorFormProps> = ({
  tractorId,
  setDefaultOnSerial,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelf();
  const canCalibrate = useCanSetDangerousParameters();
  const steps = useMemo(
    () => [
      {
        titleI18nKey: "views.autotractor.equipment.chooseTemplate",
        editedFields: [S.archetype],
        canProceed: (values: TractorViewModel) => Boolean(values.archetype),
        RenderBody: ChooseArchetypeStep,
      },
      {
        titleI18nKey: "views.autotractor.equipment.nameNoPhoto",
        editedFields: [S.name],
        RenderBody: NameAndPhotoStep,
      },
      {
        titleI18nKey: "views.autotractor.equipment.tractors.driveInfo",
        editedFields: [S.driveInfo],
        RenderBody: DriveInfoStep,
      },
      {
        titleI18nKey: "views.autotractor.equipment.tractors.hitchesTitle",
        editedFields: [S.hitches],
        RenderBody: HitchStep,
      },
      {
        titleI18nKey: "models.equipment.pto_short",
        editedFields: [S.frontPto, S.rearPto],
        RenderBody: PtoStep,
      },
      {
        titleI18nKey: "views.autotractor.equipment.implements.scvsTitle",
        editedFields: [S.scvOutputs],
        RenderBody: ScvStep,
      },
      ...(canCalibrate
        ? [
            {
              titleI18nKey: "views.autotractor.equipment.calibration",
              editedFields: [S.calibration],
              RenderBody: CalibrationStep,
            },
          ]
        : []),
      {
        titleI18nKey: "views.autotractor.equipment.review",
        editedFields: [],
        RenderBody: ReviewStep,
      },
    ],
    [canCalibrate]
  );
  const { activeStepIndex, setActiveStepIndex, activeStep, StepRenderer } =
    useFormikWizard(steps);
  const userCustomer = getCustomerId(user);
  const [customerId, setCustomerId] = useState<number | undefined>(
    userCustomer
  );
  const [setDefaultTractor] = useMutationPopups(
    portalApi.endpoints.setDefaultAtkTractor.useMutation()
  );
  const isEditing = Boolean(tractorId);

  const {
    data: tractorsResp,
    isError,
    isLoading,
  } = useQueryPopups(
    atkEquipmentApi.endpoints.searchTractors.useQuery(
      {
        tractorId,
      },
      { skip: !isEditing }
    )
  );
  const existingTractor = tractorsResp?.tractors.at(0);

  const [createTractor] = useMutationPopups(
    atkEquipmentApi.endpoints.createTractor.useMutation(),
    {
      success: capitalize(
        t("utils.actions.createdLong", {
          subject: t("models.equipment.tractor_one"),
        })
      ),
    }
  );

  const [editTractor, { isLoading: isSaving }] = useMutationPopups(
    atkEquipmentApi.endpoints.updateTractor.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.tractor_one"),
        })
      ),
    }
  );

  const customerSelectorAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );

  if (isError) {
    navigate(getTractorsPath());
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Formik
      initialValues={tractorToViewModel(existingTractor)}
      validationSchema={viewModelSchema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        if (isEditing) {
          await editTractor(viewModelToTractor(values));
        }
        if (!isEditing) {
          const res = await createTractor({
            ...viewModelToTractor(values),
            customerId: customerId?.toString(),
          });
          if (!res.error) {
            if (setDefaultOnSerial) {
              const resSetDefault = await setDefaultTractor({
                serial: setDefaultOnSerial,
                tractorId: res.data.id,
              });
              if (!resSetDefault.error) {
                navigate(getRobotPath(setDefaultOnSerial));
              }
            } else {
              navigate(getTractorsPath());
            }
          }
        }
        setSubmitting(false);
      }}
    >
      {(formikProps) => {
        const editingUnsupportedVersion = !editingSupported(formikProps.values);
        const disableNextButton =
          ((activeStep?.canProceed &&
            !activeStep.canProceed(formikProps.values)) ||
            activeStepIndex === steps.length - 1) &&
          !isEditing;
        const buttonRow = (
          <div className="flex justify-between">
            {activeStepIndex > 0 && (
              <Button
                color="inherit"
                onClick={() => setActiveStepIndex(activeStepIndex - 1)}
              >
                {t("utils.actions.backShort")}
              </Button>
            )}
            <div className="flex ml-auto gap-2">
              {activeStepIndex !== steps.length - 1 && (
                <Button
                  className="ml-auto"
                  color={isEditing ? "inherit" : "primary"}
                  variant={isEditing ? "text" : "contained"}
                  disabled={disableNextButton}
                  onClick={() =>
                    validateThen(
                      formikProps,
                      () => setActiveStepIndex(activeStepIndex + 1),
                      undefined,
                      activeStep?.editedFields ?? []
                    )
                  }
                >
                  {t("utils.actions.next")}
                </Button>
              )}
              {(isEditing || activeStepIndex === steps.length - 1) && (
                <Button
                  className="ml-auto"
                  type="submit"
                  variant="contained"
                  endIcon={
                    isSaving && (
                      <CircularProgress size="small" variant="indeterminate" />
                    )
                  }
                  disabled={
                    !formikProps.isValid ||
                    !formikProps.dirty ||
                    formikProps.isValidating ||
                    formikProps.isSubmitting ||
                    isSaving
                  }
                >
                  {isEditing
                    ? t("utils.actions.save")
                    : t("utils.actions.submit")}
                </Button>
              )}
            </div>
          </div>
        );

        return (
          <Form className="flex flex-col gap-2">
            {customerSelectorAuthorized && !isEditing && (
              <CustomerSelector
                value={customerId}
                onChange={(c) => setCustomerId(c)}
              />
            )}
            <Stepper nonLinear={isEditing} activeStep={activeStepIndex}>
              {steps.map((s, i) => (
                <Step
                  key={`${i}-${s.titleI18nKey}`}
                  index={i}
                  completed={!isEditing && activeStepIndex > i}
                >
                  <StepButton
                    color={activeStepIndex === i ? "primary" : "inherit"}
                    onClick={() =>
                      i > activeStepIndex
                        ? validateThen(
                            formikProps,
                            () => setActiveStepIndex(i),
                            undefined,
                            activeStep?.editedFields ?? []
                          )
                        : setActiveStepIndex(i)
                    }
                  >
                    {t(s.titleI18nKey)}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
            {buttonRow}
            {editingUnsupportedVersion && (
              <Alert icon={<LockIcon fontSize="inherit" />} severity="warning">
                <AlertTitle>
                  {t(
                    "views.autotractor.equipment.unsupportedVersion.tractorLockedTitle"
                  )}
                </AlertTitle>
                {t(
                  "views.autotractor.equipment.unsupportedVersion.tractorLockedBody"
                )}
              </Alert>
            )}
            <StepRenderer disabled={editingUnsupportedVersion} />
            {buttonRow}
          </Form>
        );
      }}
    </Formik>
  );
};
